/**
 * OfflineIndicator - Indicador de estado de conexion
 *
 * Muestra un banner cuando el usuario esta offline.
 * Diseñado para neurodivergencia:
 * - No intrusivo pero visible
 * - Mensaje claro y tranquilizador
 * - Desaparece automaticamente al reconectar
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, Wifi } from 'lucide-react';

export function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [showReconnected, setShowReconnected] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Mostrar brevemente mensaje de reconexion
      setShowReconnected(true);
      setTimeout(() => setShowReconnected(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowReconnected(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <AnimatePresence mode="wait">
      {/* Banner de offline */}
      {!isOnline && (
        <motion.div
          key="offline"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="fixed top-0 left-0 right-0 z-50 safe-top"
        >
          <div className="bg-amber-900/95 backdrop-blur-sm border-b border-amber-700 px-4 py-3">
            <div className="flex items-center justify-center gap-3 max-w-4xl mx-auto">
              <WifiOff className="w-4 h-4 text-amber-300 flex-shrink-0" />
              <div className="text-center">
                <p className="text-sm font-medium text-amber-100">
                  Sin conexion a internet
                </p>
                <p className="text-xs text-amber-300/80">
                  La app sigue funcionando. Tus datos se guardaran localmente.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Banner temporal de reconexion */}
      {isOnline && showReconnected && (
        <motion.div
          key="online"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="fixed top-0 left-0 right-0 z-50 safe-top"
        >
          <div className="bg-emerald-900/95 backdrop-blur-sm border-b border-emerald-700 px-4 py-2">
            <div className="flex items-center justify-center gap-2 max-w-4xl mx-auto">
              <Wifi className="w-4 h-4 text-emerald-300" />
              <p className="text-sm text-emerald-100">
                Conexion restaurada
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Hook para detectar estado de conexion
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
